import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import ClientAddressForm from '@/components/views/ClientView/ClientAddressForm.vue';
import AddressValidationStatus from '@/components/global/client/AddressValidationStatus.vue';
import AddressConfig from '@/models/interfaces/Address';
import store from '@/store';

interface Props {
    clientAddresses: AddressConfig[];
    isNewClient: boolean;
    addressTypes: string[];
    canAccessLogistics: boolean;
    isRoltek: boolean;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'ClientAddressCarousel',
  props: {
    clientAddresses: null,
    isNewClient: { type: Boolean },
    addressTypes: null,
    canAccessLogistics: { type: Boolean },
    isRoltek: { type: Boolean }
  },
  emits: ["add-address", "remove-address", "update-address", "validate-address", "open-address-in-maps", "default-address-change"],
  setup(__props: any, { expose, emit }: { emit: ({
    (e: 'add-address'): void;
    (e: 'remove-address', index: number, address: AddressConfig): void;
    (e: 'update-address', index: number, address: AddressConfig): void;
    (e: 'validate-address', index: number, address: AddressConfig, restoreFocus?: () => void): void;
    (e: 'open-address-in-maps', address: AddressConfig): void;
    (e: 'default-address-change', address: AddressConfig, isDefault: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    clientAddresses: AddressConfig[];
    isNewClient: boolean;
    addressTypes: string[];
    canAccessLogistics: boolean;
    isRoltek: boolean;
};





const activeIndex = ref(0);
const direction = ref('carousel-next');
const validationStatusCounter = ref(0);

const googleAPIQuota = computed(() => {
    return store.getters['generic/validationRequestsRemaining'];
});
const isValidationQuotaZero = computed(() => googleAPIQuota.value === 0);

const shouldShowUnavailableWarning = computed(() => isValidationQuotaZero.value && validationStatusCounter.value === 1);

const shouldShowValidationStatus = computed(() => validationStatusCounter.value < 2 && props.isRoltek);

function onAddressInput(address: AddressConfig) {
    if (!props.isRoltek) {
        return;
    }

    if (isValidationQuotaZero.value && validationStatusCounter.value === 0) {
        address.validated = false;
        validationStatusCounter.value = 1;
        emit('update-address', activeIndex.value, address);
    }
}

function onAddressBlur() {
    if (!props.isRoltek) {
        return;
    }

    if (isValidationQuotaZero.value && validationStatusCounter.value > 0) {
        validationStatusCounter.value++;
    }
}

function prev() {
    if (activeIndex.value > 0) {
        direction.value = 'carousel-prev';
        activeIndex.value--;
    }
}

function next() {
    if (activeIndex.value < props.clientAddresses.length - 1) {
        direction.value = 'carousel-next';
        activeIndex.value++;
    }
}

function onAddAddress() {
    emit('add-address');

    direction.value = 'carousel-next';
    setTimeout(() => {
        activeIndex.value = props.clientAddresses.length - 1;
    }, 0);
}

function onRemoveAddress(index: number, address: AddressConfig) {
    emit('remove-address', index, address);

    direction.value = 'carousel-prev';
    setTimeout(() => {
        if (activeIndex.value >= props.clientAddresses.length) {
            activeIndex.value = Math.max(0, props.clientAddresses.length - 1);
        }
    }, 0);
}

function onValidateAddress(address: AddressConfig, restoreFocus?: () => void) {
    if (isValidationQuotaZero.value || !props.isRoltek) {
        return;
    }

    emit('validate-address', activeIndex.value, address, restoreFocus);
}

function onOpenActiveAddressInMaps() {
    emit('open-address-in-maps', props.clientAddresses[activeIndex.value]);
}

const uniqueAddressTypes = computed(() => {
    const map = new Map<string, any>();

    props.addressTypes.forEach((type: any) => {
        map.set(type.code, type); // 👈 unique by `code`
    });

    return [...map.values()];
});

function getFilteredTypes(address: AddressConfig) {
    const currentType = address?.addressType;
    const usedTypes = new Set(props.clientAddresses.map((address: AddressConfig) => address.addressType));

    const available = uniqueAddressTypes.value.filter((type: any) => !usedTypes.has(type.id));

    if (currentType) {
        return [uniqueAddressTypes.value.find((type: any) => type.id === currentType), ...available];
    }

    return available;
}

expose({ activeIndex });

return { __sfc: true,props, emit, activeIndex, direction, validationStatusCounter, googleAPIQuota, isValidationQuotaZero, shouldShowUnavailableWarning, shouldShowValidationStatus, onAddressInput, onAddressBlur, prev, next, onAddAddress, onRemoveAddress, onValidateAddress, onOpenActiveAddressInMaps, uniqueAddressTypes, getFilteredTypes, ClientAddressForm, AddressValidationStatus }
}

})